import * as React from 'react';
import { Form, Formik } from 'formik';
import { VerificationCodeInput } from '../VerificationCodeInput';
import * as Yup from 'yup';

interface Props {
  onSubmit: (data: { emailCode: string }) => void;
  isConfirmed: boolean;
  email: string;
  onSendCodeAgainClick: (event: any) => void;
  onChangeClick: (event: any) => void;
  resendCooldownSec?: number;
  verifyCooldownSec?: number;
  verifyErrorText?: string;
  formatMmSs?: (s: number) => string;
}

function fallbackFormatMmSs(totalSec: number) {
  const s = Math.max(0, Math.floor(totalSec));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  const ss2 = ss < 10 ? `0${ss}` : String(ss);
  return `${mm}:${ss2}`;
}

function getStatus(isConfirmed: boolean, resendCooldownSec: number, verifyCooldownSec: number) {
  if (isConfirmed) return { key: 'verified', text: 'Verified' as const };
  return { key: 'pending', text: 'Code sent' as const };
}

export function EmailVerificationForm(props: Props) {
  const {
    onSubmit,
    isConfirmed,
    email,
    onSendCodeAgainClick,
    onChangeClick,
    resendCooldownSec,
    verifyCooldownSec,
    verifyErrorText,
    formatMmSs
  } = props;

  const resendCooldown = Number(resendCooldownSec || 0) || 0;
  const verifyCooldown = Number(verifyCooldownSec || 0) || 0;
  const isResendCooldown = resendCooldown > 0;
  const isVerifyCooldown = verifyCooldown > 0;
  const fmt = formatMmSs || fallbackFormatMmSs;
  const status = getStatus(!!isConfirmed, resendCooldown, verifyCooldown);
  const showExternalError = !!verifyErrorText && verifyCooldown <= 0;

  const Schema = Yup.object().shape({
    emailCode: Yup.string().required('Please enter your email verification code.')
  });

  return (
    <section className={`eVerifyCard ${isConfirmed ? 'mConfirmed' : ''}`}>
      <header className="eVerifyCardHeader">
        <div className="eVerifyCardHeaderLeft">
          <div className="eVerifyCardTitle">Email</div>
          <div className="eVerifyCardSubTitle">{email}</div>
        </div>

        <span className={`eStatusPill m-${status.key}`}>
          <span className="eStatusDot" />
          {status.text}
        </span>
      </header>

      {isConfirmed ? (
        <div className="eVerifyCardBody">
          <div className="eVerifyConfirmedBox">
            <span className="eVerifyConfirmedIcon">✓</span>
            <div>
              <div className="eVerifyConfirmedTitle">Email verified</div>
              <div className="eVerifyConfirmedText">Your email address has been confirmed successfully.</div>
            </div>
          </div>
        </div>
      ) : (
        <Formik
          initialValues={{ emailCode: '' }}
          validationSchema={Schema}
          onSubmit={values => {
            if (isVerifyCooldown) return;
            const code = String(values.emailCode || '').trim();
            onSubmit({ emailCode: code });
          }}
          validateOnBlur={false}
          validateOnChange={false}
        >
          {({ touched, errors, values, setFieldValue }) => {
            return (
              <Form className="eVerifyCardBody">
                <div className="eVerifyInputRow">
                  <VerificationCodeInput
                    label="Email verification code"
                    value={values.emailCode}
                    onChange={code => setFieldValue('emailCode', code)}
                  />
                  <button className="eVerifyPrimaryBtn" disabled={isVerifyCooldown} type="submit">
                    {isVerifyCooldown ? `Verify email (${fmt(verifyCooldown)})` : 'Verify email'}
                  </button>
                </div>

                {touched.emailCode && errors.emailCode && (
                  <div className="eVerifyInlineError">{String(errors.emailCode)}</div>
                )}

                {showExternalError && <div className="eVerifyInlineError">{verifyErrorText}</div>}

                <div className="eVerifyMetaRow">
                  <button
                    className="eVerifyLinkBtn"
                    type="button"
                    onClick={e => {
                      if (isResendCooldown) {
                        e.preventDefault();
                        return;
                      }
                      onSendCodeAgainClick(e);
                    }}
                    disabled={isResendCooldown}
                  >
                    {isResendCooldown ? `Send code again (${fmt(resendCooldown)})` : 'Send code again'}
                  </button>
                  <span className="eVerifyMetaSep">•</span>
                  <button className="eVerifyLinkBtn" type="button" onClick={onChangeClick}>
                    Wrong email?
                  </button>
                </div>
              </Form>
            );
          }}
        </Formik>
      )}
    </section>
  );
}
